import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../components/saveBackdrop';
import { AppContext } from '../../contexts/app-context';
import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canWriteAccountOptout, checkUserAuthenticated, getErrorObject,
  goToRoutePage
} from '../../utils/helper';
import { GetAppToneIcon, UploadIcon } from '../../utils/icons';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel,
  TextField, Typography
} from '../../utils/material-ui-core';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

interface UploadOptoutFileProps {
  open: boolean;
  clientCompanyKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  file: "",
};

const formDefaultTouchValue = {
  file: true,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  file: Yup.string().trim().required("Optout file is required"),
});

const UploadOptoutFile = ({ open, clientCompanyKey, onCallback }: UploadOptoutFileProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, userFunctionAccess, showAlert } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [initialFormValues, setInitialFormValues] = useState(formDefaultValue);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [fileEvent, setFileEvent] = useState<any>();

  // Upload file for validation
  const uploadFile = async () => {
    formReference.current.validateForm();

    if (!formReference.current.isValid) {
      return;
    }

    if (fileEvent !== null) {
      setSaveInProgress(true);

      const payload = new FormData();
      payload.append("file", fileEvent.target.files[0]);

      try {
        await API.postfile("/api/accountoptouts/" + clientCompanyKey + "/upload", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            reset();
            refresh();
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            const errorList: ApiResponseErrorObject[] = getErrorObject(error, "program.");
            let isErrorAssignedAtFieldLevel = true;
            try {
              errorList.forEach(function (error) {
                formReference.current.setFieldError(error.title, error.message.join(" "));
              });
            } catch (e) {
              isErrorAssignedAtFieldLevel = false;
            }
            isErrorAssignedAtFieldLevel = false;
            if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
              showAlert({
                message: apiReponseError(error, "Failed to upload file."),
                severity: "error",
              });
            }
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Convert base64 string to bytes
  const base64ToBytes = (base64) => {
    var s = window.atob(base64);
    var bytes = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) {
      bytes[i] = s.charCodeAt(i);
    }
    return bytes;
  };

  // Download template file
  const downloadTemplateFile = async () => {
    try {
      await API.get("/api/accountoptouts/template/download")
        .then(async (response) => {
          if (response.data.content === null) {
            showAlert({ message: "Unable to download report.", severity: "error" });
            return;
          }

          var blob = new Blob([base64ToBytes(response.data.content)]);

          const fileName = "OptoutTemplate.xlsx";
          if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, fileName);
          } else {
            const link = document.createElement("a");
            // Browsers that support HTML5 download attribute
            if (link.download !== undefined) {
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute("download", fileName);
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }

          showAlert({ message: "Downloading report.", severity: "success" });
        })
        .catch((error) => {
          showAlert({
            message: apiReponseError(error, "Failed to upload file."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Reset input file control and other variables
  const reset = () => {
    setSaveInProgress(false);
    setInitialFormValues(formDefaultValue);
  };

  // Cancel upload file
  const refresh = () => {
    onCallback({ status: "refresh", data: null });
  };

  // Cancel upload file
  const cancel = () => {
    onCallback({ status: "cancel", data: null });
  };

  useEffect(() => {
    if (open) {

      if (!canWriteAccountOptout(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"md"}>
      <DialogTitle className={globalStyles.dialogTitleContainer}>
        <Typography variant="subtitle1" component="div">
          Optout File Upload
        </Typography>
      </DialogTitle>

      <DialogContent dividers className={globalStyles.formikContainer}>
        <SaveBackdrop open={saveInProgress} />

        <Box className={pageStyles.root}>
          <Formik innerRef={formReference} initialValues={initialFormValues} validationSchema={formValidationSchema} validateOnMount={true} initialTouched={formDefaultTouchValue} validateOnBlur={true} validateOnChange={true} onSubmit={(values) => { }}>
            {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue }) => (
              <Form>
                <Grid container spacing={3} className={globalStyles.bottomSpacer}>
                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>
                        Optout file &nbsp;&nbsp;
                        <small>(CSV (tilde delimited); or Excel (xlsx)).</small>
                      </InputLabel>
                      <br />
                      <TextField
                        name="file"
                        type="file"
                        id="file"
                        value={values.file}
                        onChange={(event) => {
                          handleChange(event);
                          setFileEvent(event);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(errors.file)}
                      // helperText={errors.file ? errors.file : null}
                      />
                    </FormControl>
                    {
                      errors.file ? <Box className={globalStyles.errorText}>
                        <HtmlContent dangerouslySetInnerHTML={{ __html: errors.file }}></HtmlContent>
                      </Box> : null
                    }
                    <Box p={1}>&nbsp;</Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </DialogContent>

      <DialogActions>
        <Grid container style={{ height: "100%" }}>
          <Grid item xs={4}>
            <Button color="primary" variant="contained" className={globalStyles.buttonDefault} onClick={downloadTemplateFile}>
              <GetAppToneIcon /> Template
            </Button>
          </Grid>
          <Grid item xs={8}>
            <Box display={"flex"} justifyContent="right" alignContent="center">
              <Button color="secondary" variant="contained" className={globalStyles.buttonDefault} onClick={uploadFile}>
                <UploadIcon /> Upload
              </Button>
              &nbsp;
              <Button onClick={() => cancel()} color="secondary" className={globalStyles.buttonDefault} autoFocus>
                <u>Cancel</u>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    errormessage: {
      color: theme.palette.error.main,
    },
  })
);

export default UploadOptoutFile;
